import type React from 'react';
import { type ChangeEvent, type DragEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { closeModal } from '@/components/modals/modals.utils';
import { ModalBase } from '@/components/modals/modals-base';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Spinner from '@/components/ui/spinner';

export interface UploadArchiveFileModalProps {
  accept?: string;
  onSubmit: (files: File[]) => Promise<void> | void;
}

export const UploadArchiveFileModal: React.FC<UploadArchiveFileModalProps> = ({
  accept,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [, setDragActive] = useState(false);
  const fallbackAccept = '.png,.jpeg,.jpg,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx';

  const fileInputChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files ? Array.from(e.target.files) : [];
    setFiles(selected);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => setDragActive(false);

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    const selected = event.dataTransfer.files ? Array.from(event.dataTransfer.files) : [];
    setFiles(selected);
  };

  const submitHandler = async () => {
    if (!files.length) return;
    try {
      setLoading(true);
      await onSubmit(files);
      closeModal();
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalBase className="max-w-96">
      {loading ? (
        <div className="absolute inset-0 flex items-center justify-center bg-primary-lighter/40">
          <Spinner className="size-12 [&_*]:stroke-primary-dark" />
        </div>
      ) : null}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submitHandler();
        }}
      >
        <div className="flex w-full flex-col items-center justify-center space-y-3">
          <label
            htmlFor="archive-dropzone-file"
            className="flex h-64 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-text-secondary p-4 hover:bg-secondary"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center justify-center pb-6 pt-5">
              <svg
                className="mb-4 size-8 text-text-secondary"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 20 16"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                />
              </svg>
              {!files.length ? (
                <>
                  <p className="mb-1 text-center text-sm text-text-secondary">
                    {t('uploadDocumentText')}
                  </p>
                  <p className="text-sm text-text-secondary">{accept || fallbackAccept}</p>
                </>
              ) : (
                <p className="text-sm text-text-secondary">
                  {t('uploadedDocument')}:{' '}
                  <span className="font-semibold">
                    {files.length === 1 ? files[0]?.name : `${files.length} files selected`}
                  </span>
                </p>
              )}
            </div>
            <Input
              id="archive-dropzone-file"
              type="file"
              accept={accept || fallbackAccept}
              multiple
              className="hidden"
              onChange={fileInputChangeHandler}
            />
          </label>
        </div>
        <div className="mt-8 grid grid-cols-2 gap-4">
          <Button
            onClick={closeModal}
            type="button"
            variant="outline"
            className="border-red-500 text-red-500"
          >
            {t('cancel')}
          </Button>
          <Button type="submit" disabled={!files.length || loading} loading={loading}>
            {t('submit')}
          </Button>
        </div>
      </form>
    </ModalBase>
  );
};
